import React, { useEffect, useState } from "react";
import { Card, Table, Select, DatePicker, Space, Typography, message } from "antd";
import moment from "moment";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import FinanceService from "Services/FinanceService";
import { ReportPeriodTypeEnum } from "Constants/Enums/ReportPeriodTypeEnum";

const { Text } = Typography;

const money = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");

const PERIOD_OPTIONS = [
  { value: "", label: "All Time" },
  { value: ReportPeriodTypeEnum.Day, label: "Day" },
  { value: ReportPeriodTypeEnum.Week, label: "Week" },
  { value: ReportPeriodTypeEnum.Month, label: "Month" },
];

// Finance paid-expense report (Points 7 & 8). Only Paid expenses; follows the
// { period, data } report contract; optional period + department filters.
const FinancePayments = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [periodType, setPeriodType] = useState("");
  const [referenceDate, setReferenceDate] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [periodLabel, setPeriodLabel] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(API.department.getList);
        setDepartments(Array.isArray(res.data) ? res.data : res.data?.data || []);
      } catch (e) {
        // Department filter is optional; ignore load failure.
      }
    })();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const body = await FinanceService.getPaymentsReport(
        periodType === "" ? null : periodType,
        referenceDate,
        departmentId
      );
      const list = body && body.data !== undefined ? body.data : body;
      setRows(Array.isArray(list) ? list : []);
      setPeriodLabel(body?.period?.periodLabel || "");
    } catch (e) {
      console.error("Failed to load payments report", e);
      message.error("Failed to load payments report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType, referenceDate, departmentId]);

  const showDatePicker = periodType !== "";
  const picker =
    periodType === ReportPeriodTypeEnum.Week
      ? "week"
      : periodType === ReportPeriodTypeEnum.Month
      ? "month"
      : "date";

  const departmentOptions = [
    ...(Array.isArray(departments) ? departments : []).map((d) => ({
      value: d.id ?? d.departmentId,
      label: d.name ?? d.departmentName,
    })),
    // Executives: backend filters by Executive designation when departmentId is -1.
    { value: -1, label: "Executives" },
  ];

  const columns = [
    { title: "S. No.", dataIndex: "serialNo", key: "serialNo" },
    { title: "Employee", dataIndex: "employeeName", key: "employeeName", render: (v) => v || "-" },
    { title: "Date Generated", key: "gen", render: (_, r) => fmtDate(r.dateGenerated) },
    { title: "Date Paid", key: "paid", render: (_, r) => fmtDate(r.datePaid) },
    { title: "Final Amount", key: "amt", render: (_, r) => money(r.finalAmount, "USD") },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        title="Paid Expenses Report"
        className="card-professional"
        style={{ margin: 16 }}
        extra={
          <Space wrap>
            <Select
              placeholder="All Departments"
              allowClear
              style={{ width: 180 }}
              value={departmentId}
              onChange={(v) => setDepartmentId(v ?? null)}
              options={departmentOptions}
            />
            <Select
              value={periodType}
              style={{ width: 140 }}
              onChange={(v) => {
                setPeriodType(v);
                setReferenceDate(null);
              }}
              options={PERIOD_OPTIONS}
            />
            {showDatePicker && (
              <DatePicker
                picker={picker}
                onChange={(d) => setReferenceDate(d ? d.format("YYYY-MM-DD") : null)}
              />
            )}
          </Space>
        }
      >
        {periodLabel && (
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            Showing: {periodLabel}
          </Text>
        )}
        <Table
          className="custom-table"
          rowKey={(r) => r.serialNo ?? `${r.employeeName}-${r.datePaid}`}
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No paid expenses" }}
        />
      </Card>
    </DashboardLayout>
  );
};

export default FinancePayments;
